import { FunctionComponent, useEffect, useState } from 'react'
import {
  LayerGroup,
  LayersControl,
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  Tooltip,
  ZoomControl
} from 'react-leaflet'
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer'
import { MarkerClusterGroup } from './markercluster-group'
import { DEFAULT_CONFIG, DEFAULT_MAP_STYLES, MAP_JOB_RESOURCE_STYLE, storeIconText } from './map-constants'
import L, { LatLngExpression, MarkerCluster, DivIcon as LFDivIcon } from 'leaflet'
import { RoutesLayer } from './routes-layer'
import { MapJob, MapJobResource, MapResource, MapRoute, MapStore, MapTask } from './map-types'
import { getResourcesMarker, JobResourceMarker } from './map-utils'
import { JobMarker } from './job-marker'
import { Popup } from 'react-leaflet'
import { CustomButton } from '../custom-button/custom-button'
import { JobResume } from './job-resume'
import { ShoppingBag } from '../../icons'
import { TaskMarker } from './task-marker'
import { useTranslation } from 'react-i18next'

import 'leaflet-draw'

const { BaseLayer, Overlay } = LayersControl

/**
 * Properties type for Map
 */
export type MapProps = {
  id?: string
  routes?: MapRoute[]
  store?: MapStore
  stores?: MapStore[]
  tasks?: MapTask[]
  jobs?: MapJob[]
  mapJobResource?: MapJobResource
  resources?: MapResource[]
  initialCenter: LatLngExpression
  storeIcon?: L.Icon
  viewport?: {
    center?: LatLngExpression
    zoom?: number
  }
  zoom?: number
  timezone: string
  enableSelection?: boolean
  onSelection?: () => void
}

export const Map: FunctionComponent<MapProps> = (props: MapProps) => {
  const { t } = useTranslation()

  const mapDefaultConfig = {
    ...DEFAULT_CONFIG,
    ...props
  }

  const {
    id,
    routes,
    store,
    stores,
    tasks,
    jobs,
    resources,
    mapJobResource,
    initialCenter,
    storeIcon = new L.Icon({
      iconAnchor: [15, 30],
      popupAnchor: [10, -44],
      iconSize: [30, 36],
      iconUrl: storeIconText
    }),
    zoom = 11,
    timezone,
    enableSelection = false,
    onSelection = () => {}
  } = props

  const [center, setCenter] = useState(initialCenter)
  const [map, setMap] = useState<null | L.Map>(null)
  const [drawControl, setDrawControl] = useState<L.Control.Draw | null>(null)

  L.drawLocal.draw.toolbar.actions = {
    title: t('cancel'),
    text: t('cancel')
  }
  L.drawLocal.draw.toolbar.buttons.rectangle = t('selectTasks')
  L.drawLocal.draw.handlers.rectangle.tooltip.start = t('selectTasks')
  L.drawLocal.draw.handlers.simpleshape.tooltip.end = t('releaseDrawing')

  const createClusterIcon = (color: string, mate = true) => {
    return (cluster: MarkerCluster) => {
      const childCount = cluster.getChildCount()
      const marker = new LFDivIcon({
        html: `<div><span>${childCount}</span></div>`,
        className: `marker-cluster marker-cluster-${color} ${mate ? 'mate' : ''}`,
        iconSize: new L.Point(30, 30)
      })
      return marker
    }
  }

  const getMapJobResourcePositions = (mapJobResource: MapJobResource): LatLngExpression[] => {
    const positions: LatLngExpression[] = []
    positions.push(mapJobResource.job.location)
    if (mapJobResource.resource) {
      positions.push([mapJobResource.resource.latitude, mapJobResource.resource.longitude])
    }
    positions.push(mapJobResource.store.location)

    return positions
  }

  useEffect(() => {
    if (map && store && store.location) {
      setCenter(store.location)
      map.setView(L.latLng(store.location))
    }
  }, [store, map])

  useEffect(() => {
    if (routes && routes.length) {
      const bounds = L.featureGroup(
        routes.map(({ points }) => L.featureGroup(points.map(({ location }) => L.marker(L.latLng(location)))))
      ).getBounds()
      if (map) {
        map.fitBounds(bounds)
      }
    }
  }, [routes, map])

  useEffect(() => {
    if (jobs && jobs.length) {
      const bounds = L.featureGroup(jobs.map(({ location }) => L.marker(L.latLng(location)))).getBounds()
      if (resources?.length) {
        resources.map(
          (resource) =>
            resource?.latitude && resource?.longitude && bounds.extend(L.latLng(resource.latitude, resource.longitude))
        )
      }
      if (map) {
        map.fitBounds(bounds)
      }
    }
  }, [jobs, map, resources])

  useEffect(() => {
    if (mapJobResource) {
      const group = L.featureGroup()
      const { job, resource, store } = mapJobResource

      if (job?.location) {
        group.addLayer(L.marker(L.latLng(job.location)))
      }

      if (store?.location) {
        group.addLayer(L.marker(L.latLng(store.location)))
      }

      if (resource?.latitude && resource?.longitude) {
        group.addLayer(L.marker(L.latLng(resource.latitude, resource.longitude)))
      }

      if (map && group.getLayers().length > 0) {
        const bounds = group.getBounds()
        map.fitBounds(bounds)
      }
    }
  }, [mapJobResource, map])

  useEffect(() => {
    if (map && props.viewport && Array.isArray(props.viewport.center)) {
      map.setView(L.latLng(props.viewport.center), props.viewport.zoom)
    }
  }, [props.viewport, map])

  useEffect(() => {
    const toggleDrawControl = () => {
      if (enableSelection) {
        if (!drawControl) {
          const _drawControl = new L.Control.Draw({
            draw: {
              polygon: false,
              polyline: false,
              marker: false,
              circle: false,
              circlemarker: false
            },
            edit: {
              featureGroup: new L.FeatureGroup(),
              edit: false,
              remove: false
            }
          })
          map?.addControl(_drawControl)
          setDrawControl(_drawControl)
          map?.on(L.Draw.Event.CREATED, onSelection)
        }
      } else {
        if (drawControl) {
          map?.removeControl(drawControl)
          setDrawControl(null)
        }
      }
    }

    if (!map) {
      return
    }
    toggleDrawControl()
  }, [drawControl, enableSelection, map, onSelection])

  useEffect(() => {
    if (map && tasks && tasks.length > 0) {
      map.off(L.Draw.Event.CREATED)
      map.on(L.Draw.Event.CREATED, onSelection)
    }
  }, [map, onSelection, tasks])

  return (
    <MapContainer
      id={id}
      data-testid='gungnir-map'
      className='gungnir-map'
      {...mapDefaultConfig}
      center={center}
      zoom={zoom}
      whenCreated={setMap}
    >
      <ZoomControl position='bottomright' />
      {store && (
        <Marker position={store.location} icon={storeIcon}>
          {!store.hideTooltip ? (
            <Tooltip direction='bottom' permanent>
              <b>{store.name}</b>
            </Tooltip>
          ) : null}
        </Marker>
      )}
      <LayersControl position='topright'>
        <BaseLayer checked name={t('googlemapsRoadmap')}>
          <ReactLeafletGoogleLayer type={'roadmap'} useGoogMapsLoader={false} styles={DEFAULT_MAP_STYLES} />
        </BaseLayer>
        <BaseLayer name={t('googlemapsTraffic')}>
          <ReactLeafletGoogleLayer
            type={'roadmap'}
            useGoogMapsLoader={false}
            googleMapsAddLayers={[{ name: 'TrafficLayer' }]}
          />
        </BaseLayer>
        <BaseLayer name={t('openstreetmap')}>
          <TileLayer
            attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
        </BaseLayer>
        {routes && routes.length ? (
          <Overlay checked name={t('routes')}>
            <LayerGroup>
              {routes.map((route, i) => {
                const resource =
                  route.hasOwnProperty('resource') &&
                  (() => {
                    const styles = `background: ${route.color.base};box-shadow: 0px 0px 4px 4px ${route.color.mate};`
                    return getResourcesMarker(i + 1, route.resource, 'black', styles)
                  })()
                return <RoutesLayer route={route} i={i} resource={resource} key={`map-route-${i}`} />
              })}
              {store && (
                <Marker position={store.location} icon={storeIcon}>
                  {!store.hideTooltip ? (
                    <Tooltip direction='bottom' permanent>
                      <b>{store.name}</b>
                    </Tooltip>
                  ) : null}
                </Marker>
              )}
            </LayerGroup>
          </Overlay>
        ) : null}
        {stores && stores.length && (
          <Overlay checked name={t('stores')}>
            <LayerGroup>
              {stores.map((s, i) => (
                <Marker key={i.toString()} position={s.location} icon={storeIcon}>
                  {s.jobs && (
                    <>
                      <Popup className='gungnir-jm-store-popup' closeButton={false} offset={[-9, 32]} maxHeight={300}>
                        <div className='gungnir-jm-jobs'>
                          {s.jobs.map((job, idx) => {
                            return (
                              <JobResume
                                key={`${job.id}-${idx}`}
                                job={job}
                                onJobClick={() => (job.onJobClick ? job.onJobClick() : null)}
                                icon={<ShoppingBag />}
                                timezone={timezone}
                              />
                            )
                          })}
                        </div>
                        <div className='gungnir-popup-footer'>
                          <CustomButton buttonText='Ver más' type='primary'></CustomButton>
                        </div>
                      </Popup>
                      <Tooltip className='gungnir-store-tooltip' direction='bottom'>
                        <b>{s.name}</b>
                        <div className='gungnir-jm-gray'>{`${s.jobs.length} ${t('orders')}`}</div>
                      </Tooltip>
                    </>
                  )}
                </Marker>
              ))}
            </LayerGroup>
          </Overlay>
        )}
        {tasks && (
          <Overlay checked name={t('tasks')}>
            <LayerGroup>
              <MarkerClusterGroup iconCreateFunction={createClusterIcon('doger-blue')}>
                {tasks.map((task, idx) => {
                  const styles = task.selected ? 'border: 1px solid #A0A1A2;background: #1890FF;color: white;' : ''
                  const random = Math.random() * 10000
                  return (
                    <TaskMarker
                      key={`task-marker-${random}-${idx + 1}`}
                      task={task}
                      num={idx + 1}
                      styles={styles}
                      onTaskClick={(_task) => {
                        task.onTaskClick && task.onTaskClick(_task)
                      }}
                    />
                  )
                })}
              </MarkerClusterGroup>
            </LayerGroup>
          </Overlay>
        )}
        {resources && (
          <Overlay checked name={t('resources')}>
            <LayerGroup>
              <MarkerClusterGroup iconCreateFunction={createClusterIcon('black')}>
                {resources.map((resource, idx) => {
                  const color = resource.selected ? 'black' : 'white'
                  return getResourcesMarker(idx + 1, resource, color)
                })}
              </MarkerClusterGroup>
            </LayerGroup>
          </Overlay>
        )}
        {jobs && (
          <Overlay checked name={t('jobs')}>
            <LayerGroup>
              <MarkerClusterGroup iconCreateFunction={createClusterIcon('black', false)}>
                {jobs.map((job, idx) => {
                  return (
                    <JobMarker
                      job={job}
                      key={`job-marker-${job.id}-${idx}`}
                      onJobClick={() => {
                        job.onJobClick && job.onJobClick()
                      }}
                      onOpenPopup={(_job) => {
                        job.onOpenPopup && job.onOpenPopup(_job)
                      }}
                      onClosePopup={() => {
                        job.onClosePopup && job.onClosePopup()
                      }}
                      timezone={timezone}
                    ></JobMarker>
                  )
                })}
              </MarkerClusterGroup>
            </LayerGroup>
          </Overlay>
        )}
        {mapJobResource && (
          <Overlay checked name={t('mapresources')}>
            <LayerGroup>
              <Polyline positions={getMapJobResourcePositions(mapJobResource)} {...MAP_JOB_RESOURCE_STYLE} />
              {mapJobResource.resource && <JobResourceMarker resource={mapJobResource.resource}></JobResourceMarker>}
              <JobMarker
                job={mapJobResource.job}
                onJobClick={() => {
                  mapJobResource.job.onJobClick && mapJobResource.job.onJobClick()
                }}
                timezone={timezone}
              />
              <Marker position={mapJobResource.store.location} icon={storeIcon} />
            </LayerGroup>
          </Overlay>
        )}
      </LayersControl>
    </MapContainer>
  )
}
